// Client API layer. Talks to the deployed Worker (CORS is open), so this works the same
// from the local dev preview and from Pages.

export const API = 'https://oxide-server.alexgraham1016.workers.dev';

export interface ServerInfo {
  id: string;
  name: string;
  region: string;
  maxPlayers: number;
  seed?: number; // world seed: the client sets TERRAIN/CLIMATE seed from this before generating the map
  players?: number | null; // LIVE connected-account count from /api/servers (null = the DO was unreachable this fetch)
}

// The owner's account ids (main + the MP-testing alt). The client only uses these to SHOW the admin controls;
// the server independently enforces the same allowlist on every create/wipe, so hiding the buttons is convenience,
// not security. Must stay in sync with OWNER_IDS in server/src/index.ts.
export const ADMIN_ACCOUNT_IDS: readonly string[] = [
  '246585c5-2411-4596-b036-e6da5e0fde30', // iacon
  '5424c3f9-18b8-4e87-9ebd-1150d981b955', // iacontester (owner alt)
];
export function isAdminAccount(account: Account | null, isDev: boolean): boolean {
  return isDev || (!!account && ADMIN_ACCOUNT_IDS.includes(account.id));
}

export interface Account {
  id: string;
  name: string;
  token?: string; // session token minted at email-verify; required by the world socket + admin calls (anti-spoofing)
  isMod?: boolean; // MOD class (decoupled from ADMIN_ACCOUNT_IDS): fetched fresh from /api/auth/check each
                    // preflight, never persisted -- see main.ts preflightConnect + ui/modAccess.ts
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(API + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : 'Request failed.');
  return data as T;
}

export function requestCode(email: string): Promise<{ devCode?: string }> {
  return postJson('/api/auth/request', { email });
}

export function verifyCode(email: string, code: string): Promise<{ id: string; displayName: string | null; token?: string }> {
  return postJson('/api/auth/verify', { email, code });
}

export function setDisplayName(id: string, name: string): Promise<{ id: string; displayName: string; token?: string }> {
  return postJson('/api/auth/name', { id, name });
}

export async function getServers(): Promise<ServerInfo[]> {
  const res = await fetch(API + '/api/servers');
  if (!res.ok) throw new Error('Could not load servers.');
  const data = (await res.json()) as { servers: ServerInfo[] };
  return data.servers;
}

// REAL per-server ping: round-trip to ONE world's DO through the worker's /count proxy -- the actual join-path
// latency for that server (edge + that DO's pinned region), not a number synthesized from the region label.
// Two-step: the first request rides the TLS handshake + a cold DO wake, the SECOND is the measurement. A fresh
// live player count comes back for free. null ping = unreachable (render as offline).
export async function pingServer(id: string): Promise<{ ping: number | null; players: number | null }> {
  const url = `${API}/api/world/${encodeURIComponent(id)}/count`;
  try {
    const warm = await fetch(url);
    if (!warm.ok) return { ping: null, players: null };
    const t0 = performance.now();
    const res = await fetch(url);
    const ping = Math.round(performance.now() - t0);
    if (!res.ok) return { ping: null, players: null };
    const data = (await res.json().catch(() => ({}))) as { count?: number };
    return { ping, players: typeof data.count === 'number' ? data.count : null };
  } catch { return { ping: null, players: null }; }
}

// Admin: create a new world or wipe one. The server verifies the session TOKEN -- the account id alone
// proves nothing (the owner UUIDs are public in this bundle). `seed` (optional): the New World popup lets
// the admin PREVIEW generations and only accept a non-broken one, so the accepted seed must be what ships.
// `timeFrac`/`timeLocked` (optional): pin the new world's day-night clock at creation (a dedicated
// always-lit/dark test world) instead of it free-running off the shared elapsed clock.
export async function adminCreateWorld(account: Account, name: string, seed?: number, timeFrac?: number, timeLocked?: boolean): Promise<ServerInfo> {
  const data = await postJson<{ server: ServerInfo }>('/api/admin/create', { accountId: account.id, token: account.token, name, seed, timeFrac, timeLocked });
  return data.server;
}
export function adminWipeWorld(account: Account, id: string): Promise<{ ok: boolean }> {
  return postJson('/api/admin/wipe', { accountId: account.id, token: account.token, id });
}
export function adminDeleteWorld(account: Account, id: string): Promise<{ ok: boolean }> {
  return postJson('/api/admin/delete', { accountId: account.id, token: account.token, id });
}
// Admin: pin (locked=true) or free (locked=false) an EXISTING world's day-night clock -- e.g. un-sticking
// a test world that's stuck at night mid-session, without having to recreate it.
export function adminSetWorldClock(account: Account, id: string, frac: number, locked: boolean): Promise<{ ok: boolean }> {
  return postJson('/api/admin/clock', { accountId: account.id, token: account.token, id, frac, locked });
}

// One-click DEV sign-in (localhost/Preview): the server mints an owner-level token when DEV_LOGIN is on.
// Returns null if dev login is disabled (prod), so the caller falls back to the normal auth screen.
export async function devLogin(): Promise<Account | null> {
  try {
    const data = await postJson<{ id: string; displayName: string; token?: string }>('/api/auth/dev', {});
    return { id: data.id, name: data.displayName || 'Dev', token: data.token };
  } catch { return null; }
}

const ACCOUNT_KEY = 'oxide.account';

export function loadAccount(): Account | null {
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Partial<Account>;
    return a.id && a.name ? { id: a.id, name: a.name, token: a.token } : null;
  } catch {
    return null;
  }
}

export function saveAccount(a: Account): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(a));
  } catch {
    // ignore
  }
}

export function clearAccount(): void {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    // ignore
  }
}
